import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import withRouter from "components/Common/withRouter"
import SelectVideoRoom from "components/DropdownList/SelectVideoRoom"
import {
  getVideoRoomLoginJson,
  sendVideoAppSettings,
} from "helpers/backend_helper"
import { getLocale, showToast } from "utils/general"
import {
  Row,
  Col,
  Form,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Alert,
} from "reactstrap"
import {
  Divider,
  InputNumber,
  TimePicker,
  ConfigProvider,
  Select,
  Steps,
  Slider,
  Popconfirm,
  Form as AntdForm,
  Input as AntdInput,
} from "antd"
import * as Yup from "yup"
import { useFormik } from "formik"
import { isEqual } from "lodash"
import { Chip } from "@material-ui/core"
import SimpleBar from "simplebar-react"
import { Link } from "react-router-dom"
const { TextArea } = AntdInput

const VideoAppSettingsModal = props => {
  const locale = getLocale()
  const [update, setUpdate] = useState(false)
  const [changedValues, setChangedValues] = useState({})
  const [changedProps, setChangedProps] = useState(0)
  const [changesTable, setChangesTable] = useState([])
  const [changedValuesString, setChangedValuesString] = useState("")
  const [displayedNames, setDisplayedNames] = useState([])
  const [remainingNames, setRemainingNames] = useState([])
  const [showAll, setShowAll] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [selectedDemoRoom, setSelectedDemoRoom] = useState(null)

  const [dimUnit, setDimUnit] = useState("minutes")
  const [screensaverContent, setScreensaverContent] = useState("")
  const [restartTime, setRestartTime] = useState(null)
  const [daytimeRange, setDaytimeRange] = useState([null, null])
  const [gridCols] = useState([4, 8])
  const [currentStep, setCurrentStep] = useState(0)
  const modalSteps = [
    {
      title: props.t("Settings"),
    },
    {
      title: props.t("Confirm"),
    },
  ]

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (!props.showModal) {
      resetFormValues()
      setSelectedRoom(null)
      setSelectedDemoRoom(null)
    } else {
      setCurrentStep(0)
    }
  }, [props.showModal])

  useEffect(() => {
    if (props.clients?.length > 0) {
      let names = showAll ? props.clients : props.clients.slice(0, 5)
      setDisplayedNames(names)
      let remaining = props.clients.length - 5
      setRemainingNames(remaining)
    }
  }, [showAll, props.clients])

  // Settings validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      room_production: "",
      user_radio: 0,
      settings_user_name_production: "",
      settings_user_password_production: "",
      settings_website_production_url: "",

      settings_user_name_demo: "",
      settings_user_password_demo: "",
      settings_website_demo_url: "",

      usesDimMinutes: null,
      dimMinutes: null,
      dimSeconds: null,

      clockEnabled: null,
      analogClockEnabled: null,
      galleryMode: null,

      automaticPickup: "",
      answerTime: null,

      applicationPipShown: "",
      showHealthButton: "",
      showDebugStats: "",

      usesProduction: "",
      uses_assisted_restart: "",

      restartTimeHour: null,
      restartTimeMinute: null,
      restartTimeSecond: null,

      morningTimeHour: null,
      morningTimeMinute: null,
      nightTimeHour: null,
      nightTimeMinute: null,

      settings_locale_language: "",
      settings_device_screen_brightness: null,
      settings_device_volume_value: null,
      settings_device_volume_buttons_enabled: "",
    },
    validationSchema: Yup.object({
      settings_user_name_production: Yup.string().email(
        props.t("Invalid format")
      ),
      settings_user_name_demo: Yup.string().email(props.t("Invalid format")),
      settings_website_production_url: Yup.string().test(
        "ac-website-url",
        props.t("Invalid"),
        val =>
          !val ||
          (val.startsWith("https://") &&
            val.endsWith("arcticcommunicator.com/?82-1="))
      ),
      settings_website_demo_url: Yup.string().test(
        "ac-website-url",
        props.t("Invalid"),
        val =>
          !val ||
          (val.startsWith("https://") &&
            val.endsWith("arcticcommunicator.com/?82-1="))
      ),
    }),
    onSubmit: values => {
      //console.log("Submit values: ", values)
      //console.log("Changed values: ", changedValues, props.keys)
      if (props.keys && changedProps > 0 && !update) {
        if (props.setFailed) {
          props.setFailed([])
        }
        setUpdate(true)
        sendVideoAppSettings(abortRef.current, props.keys.join(), changedValues)
          .then(result => {
            console.log("Result: ", result)
            if (result.statusCode == 200 && result.sent > 0) {
              showToast(props.t("Sent"), "success")
              resetFormValues()
              props.setShowModal(false)
              if (props.setFailed && result.failed?.length > 0) {
                props.setFailed(result.failed)
              }
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            showToast(props.t("Error"), "error")
            console.log(err)
          })
          .finally(() => {
            setUpdate(false)
          })
      }
    },
  })

  useEffect(() => {
    // Get changed values compared to initial
    const initialFormValues = JSON.parse(
      JSON.stringify(validation.initialValues)
    )
    let temp = {}
    for (const key in validation.values) {
      if (!isEqual(initialFormValues[key], validation.values[key])) {
        temp[key] = validation.values[key]
      }
    }
    setChangedValues(temp)
    setChangedValuesString(JSON.stringify(temp))
    setChangedProps(Object.keys(temp).length)

    // Confirmation table content
    let table = []

    if (temp.settings_user_name_production)
      table.push({
        loc: props.t("Username"),
        value: temp.settings_user_name_production,
      })
    if (temp.settings_user_password_production)
      table.push({ loc: props.t("Password"), value: "****" })
    if (temp.settings_website_production_url)
      table.push({
        loc: props.t("URL"),
        value: temp.settings_website_production_url,
      })

    if (temp.settings_user_name_demo)
      table.push({
        loc: props.t("Username") + " - Demo",
        value: temp.settings_user_name_demo,
      })
    if (temp.settings_user_password_demo)
      table.push({ loc: props.t("Password") + " - Demo", value: "****" })
    if (temp.settings_website_demo_url)
      table.push({
        loc: props.t("URL") + " - Demo",
        value: temp.settings_website_demo_url,
      })

    if (temp.automaticPickup != null)
      table.push({
        loc: props.t("Auto answer"),
        value: temp.automaticPickup ? props.t("Enabled") : props.t("Disabled"),
      })
    if (temp.answerTime != null)
      table.push({ loc: props.t("Answer time"), value: temp.answerTime + "s" })

    if (daytimeRange?.[0])
      table.push({
        loc: props.t("Screensaver active"),
        value:
          daytimeRange[0].format("HH:mm") +
          " - " +
          daytimeRange[1].format("HH:mm"),
      })
    if (restartTime)
      table.push({
        loc: props.t("Restart time"),
        value: restartTime.format("HH:mm"),
      })

    if (screensaverContent) {
      switch (screensaverContent) {
        case "nothing":
          table.push({ loc: props.t("Screensaver"), value: props.t("Nothing") })
          break
        case "analog":
          table.push({
            loc: props.t("Screensaver"),
            value: props.t("Analog clock"),
          })
          break
        case "digital":
          table.push({
            loc: props.t("Screensaver"),
            value: props.t("Digital clock"),
          })
          break
      }
    }

    if (temp.usesDimMinutes != null) {
      if (temp.usesDimMinutes) {
        table.push({
          loc: props.t("Screensaver delay"),
          value: temp.dimMinutes + "min",
        })
      } else {
        table.push({
          loc: props.t("Screensaver delay"),
          value: temp.dimSeconds + "s",
        })
      }
    }

    if (temp.settings_locale_language != null) {
      switch (temp.settings_locale_language) {
        case "FI":
          table.push({ loc: props.t("Language"), value: props.t("Finnish") })
          break
        case "EN":
          table.push({ loc: props.t("Language"), value: props.t("English") })
          break
        case "SV":
          table.push({ loc: props.t("Language"), value: props.t("Swedish") })
          break
      }
    }

    if (temp.settings_device_volume_value != null)
      table.push({
        loc: props.t("Volume"),
        value: temp.settings_device_volume_value * 10 + "%",
      })
    if (temp.settings_device_screen_brightness != null)
      table.push({
        loc: props.t("Screen brightness"),
        value: Math.round(temp.settings_device_screen_brightness * 100) + "%",
      })

    if (temp.settings_device_volume_buttons_enabled != null)
      table.push({
        loc: props.t("Volume buttons"),
        value: temp.settings_device_volume_buttons_enabled
          ? props.t("Enabled")
          : props.t("Disabled"),
      })
    if (temp.applicationPipShown != null)
      table.push({
        loc: "PiP",
        value: temp.applicationPipShown
          ? props.t("Enabled")
          : props.t("Disabled"),
      })
    if (temp.showHealthButton != null)
      table.push({
        loc: "Health",
        value: temp.showHealthButton ? props.t("Enabled") : props.t("Disabled"),
      })
    if (temp.showDebugStats != null)
      table.push({
        loc: "Debug",
        value: temp.showDebugStats ? props.t("Enabled") : props.t("Disabled"),
      })
    if (temp.uses_assisted_restart != null)
      table.push({
        loc: props.t("Assisted restart"),
        value: temp.uses_assisted_restart
          ? props.t("Enabled")
          : props.t("Disabled"),
      })
    if (temp.usesProduction != null)
      table.push({
        loc: props.t("Platform"),
        value: temp.usesProduction ? props.t("Production") : props.t("Demo"),
      })

    table.sort((a, b) => {
      return a.loc.localeCompare(b.loc)
    })
    setChangesTable(table)
  }, [validation.values])

  // Video room selection changed
  useEffect(() => {
    validation.setFieldValue("settings_user_name_production", "")
    validation.setFieldValue("settings_user_password_production", "")
    validation.setFieldValue("settings_website_production_url", "")

    if (selectedRoom) {
      getVideoRoomLoginJson(abortRef.current, selectedRoom).then(result => {
        if (result.statusCode == 200 && result.data) {
          validation.setFieldValue(
            "settings_user_name_production",
            result.data.username
          )
          validation.setFieldValue(
            "settings_user_password_production",
            result.data.password
          )
          validation.setFieldValue(
            "settings_website_production_url",
            result.data.url
          )
        }
      })
    }
  }, [selectedRoom])

  // Video demo room selection changed
  useEffect(() => {
    validation.setFieldValue("settings_user_name_demo", "")
    validation.setFieldValue("settings_user_password_demo", "")
    validation.setFieldValue("settings_website_demo_url", "")

    if (selectedDemoRoom) {
      getVideoRoomLoginJson(abortRef.current, selectedDemoRoom).then(result => {
        if (result.statusCode == 200 && result.data) {
          validation.setFieldValue(
            "settings_user_name_demo",
            result.data.username
          )
          validation.setFieldValue(
            "settings_user_password_demo",
            result.data.password
          )
          validation.setFieldValue("settings_website_demo_url", result.data.url)
        }
      })
    }
  }, [selectedDemoRoom])

  // Toggle show all recipients
  const toggleShowAll = () => {
    setShowAll(!showAll)
  }

  // Reset form
  const resetFormValues = () => {
    validation.resetForm()
    setScreensaverContent("")
    setRestartTime(null)
    setDaytimeRange([null, null])
  }

  return (
    <>
      <Modal
        isOpen={props.showModal}
        toggle={() => {
          props.setShowModal(!props.showModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Change settings")}</ModalHeader>
        <Form
          className="form-horizontal"
          onSubmit={e => {
            e.preventDefault()
            return false
          }}
        >
          <ModalBody>
            <Steps
              type="navigation"
              size="small"
              className="pb-3 pt-0"
              //onChange={(v) => {changedProps > 0 ? setCurrentStep(v) : null}}
              current={currentStep}
              items={modalSteps}
            ></Steps>

            {currentStep == 0 && (
              <SimpleBar className="pe-3" style={{ maxHeight: "50vh" }}>
                {props.keys.length == 1 && props.isSystemAdmin && (
                  <>
                    <Divider
                      orientation="left"
                      className="font-size-14 mt-3 text-primary"
                      orientationMargin="0"
                    >
                      {props.t("User")}
                    </Divider>
                    <Row xs={1}>
                      <Col>
                        <FormGroup row>
                          <Label sm={gridCols[0]}>
                            <Link
                              to="#"
                              onClick={() => {
                                setSelectedRoom(props.clients?.[0].room)
                              }}
                            >
                              {props.t("Select linked")}
                            </Link>
                          </Label>
                          <Col sm={gridCols[1]}>
                            <div>
                              <SelectVideoRoom
                                onChange={v => {
                                  setSelectedRoom(v)
                                }}
                                selection={selectedRoom}
                                multiple={false}
                                clearable={true}
                              />
                              <div
                                className="d-flex"
                                style={{ justifyContent: "center" }}
                              >
                                <i className="mdi mdi-arrow-down-bold font-size-20 text-muted" />
                              </div>
                            </div>
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row style={{ marginTop: "-1rem" }}>
                          <Label sm={gridCols[0]}>{props.t("Username")}</Label>
                          <Col sm={gridCols[1]}>
                            <AntdForm.Item
                              style={{ marginBottom: "0px" }}
                              validateStatus={
                                validation.errors.settings_user_name_production
                                  ? "error"
                                  : ""
                              }
                              hasFeedback
                              help={
                                validation.errors.settings_user_name_production
                              }
                            >
                              <AntdInput
                                allowClear
                                placeholder={"room-number@suvanto.video"}
                                value={
                                  validation.values
                                    .settings_user_name_production
                                }
                                onChange={v => {
                                  validation.setFieldValue(
                                    "settings_user_name_production",
                                    v.target.value
                                  )
                                }}
                              />
                            </AntdForm.Item>
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row>
                          <Label sm={gridCols[0]}>{props.t("Password")}</Label>
                          <Col sm={gridCols[1]}>
                            <AntdForm.Item
                              style={{ marginBottom: "0px" }}
                              validateStatus={
                                validation.errors
                                  .settings_user_password_production
                                  ? "error"
                                  : ""
                              }
                              hasFeedback
                              help={
                                validation.errors
                                  .settings_user_password_production
                              }
                            >
                              <AntdInput.Password
                                allowClear
                                placeholder={props.t("Password")}
                                value={
                                  validation.values
                                    .settings_user_password_production
                                }
                                onChange={v => {
                                  validation.setFieldValue(
                                    "settings_user_password_production",
                                    v.target.value
                                  )
                                }}
                              />
                            </AntdForm.Item>
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row>
                          <Label sm={gridCols[0]}>{props.t("URL")}</Label>
                          <Col sm={gridCols[1]}>
                            <div>
                              <Select
                                value={
                                  validation.values
                                    .settings_website_production_url
                                }
                                onChange={v => {
                                  validation.setFieldValue(
                                    "settings_website_production_url",
                                    v
                                  )
                                }}
                                options={[
                                  {
                                    label:
                                      props.t("Undefined") +
                                      " / " +
                                      props.t("Other"),
                                    value: "",
                                  },
                                  {
                                    label: props.t("Classic"),
                                    value:
                                      "https://suvantocare-webrtcapi.arcticcommunicator.com/?82-1=",
                                  },
                                  {
                                    label: props.t("Classic demo"),
                                    value:
                                      "https://suvantocare-webrtcapidemo.arcticcommunicator.com/?82-1=",
                                  },
                                  {
                                    label: props.t("Istekki"),
                                    value:
                                      "https://istekki.arcticcommunicator.com/?82-1=",
                                  },
                                  {
                                    label: props.t("Istekki demo"),
                                    value:
                                      "https://istekkidemo.arcticcommunicator.com/?82-1=",
                                  },
                                ]}
                                style={{ width: 100 + "%" }}
                              />
                              <AntdForm.Item
                                style={{ marginBottom: "0px" }}
                                validateStatus={
                                  validation.errors
                                    .settings_website_production_url
                                    ? "error"
                                    : ""
                                }
                                hasFeedback
                                help={
                                  validation.errors
                                    .settings_website_production_url
                                }
                              >
                                <TextArea
                                  className="mt-2"
                                  allowClear
                                  autoSize={{ minRows: 3, maxRows: 5 }}
                                  placeholder={props.t("URL")}
                                  value={
                                    validation.values
                                      .settings_website_production_url
                                  }
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "settings_website_production_url",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </div>
                          </Col>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Divider
                      orientation="left"
                      className="font-size-14 mt-3 text-primary"
                      orientationMargin="0"
                    >
                      {props.t("User") + " - Demo"}
                    </Divider>
                    <Row xs={1}>
                      <Col>
                        <FormGroup row>
                          <Label sm={gridCols[0]}>
                            <Link
                              to="#"
                              onClick={() => {
                                setSelectedDemoRoom(props.clients?.[0].room)
                              }}
                            >
                              {props.t("Select linked")}
                            </Link>
                          </Label>
                          <Col sm={gridCols[1]}>
                            <div>
                              <SelectVideoRoom
                                onChange={v => {
                                  setSelectedDemoRoom(v)
                                }}
                                selection={selectedDemoRoom}
                                multiple={false}
                                clearable={true}
                              />
                              <div
                                className="d-flex"
                                style={{ justifyContent: "center" }}
                              >
                                <i className="mdi mdi-arrow-down-bold font-size-20 text-muted" />
                              </div>
                            </div>
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row style={{ marginTop: "-1rem" }}>
                          <Label sm={gridCols[0]}>{props.t("Username")}</Label>
                          <Col sm={gridCols[1]}>
                            <AntdForm.Item
                              style={{ marginBottom: "0px" }}
                              validateStatus={
                                validation.errors.settings_user_name_demo
                                  ? "error"
                                  : ""
                              }
                              hasFeedback
                              help={validation.errors.settings_user_name_demo}
                            >
                              <AntdInput
                                allowClear
                                placeholder={"room-number@suvanto.video"}
                                value={
                                  validation.values.settings_user_name_demo
                                }
                                onChange={v => {
                                  validation.setFieldValue(
                                    "settings_user_name_demo",
                                    v.target.value
                                  )
                                }}
                              />
                            </AntdForm.Item>
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row>
                          <Label sm={gridCols[0]}>{props.t("Password")}</Label>
                          <Col sm={gridCols[1]}>
                            <AntdForm.Item
                              style={{ marginBottom: "0px" }}
                              validateStatus={
                                validation.errors.settings_user_password_demo
                                  ? "error"
                                  : ""
                              }
                              hasFeedback
                              help={
                                validation.errors.settings_user_password_demo
                              }
                            >
                              <AntdInput.Password
                                allowClear
                                placeholder={props.t("Password")}
                                value={
                                  validation.values.settings_user_password_demo
                                }
                                onChange={v => {
                                  validation.setFieldValue(
                                    "settings_user_password_demo",
                                    v.target.value
                                  )
                                }}
                              />
                            </AntdForm.Item>
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row>
                          <Label sm={gridCols[0]}>{props.t("URL")}</Label>
                          <Col sm={gridCols[1]}>
                            <div>
                              <Select
                                value={
                                  validation.values.settings_website_demo_url
                                }
                                onChange={v => {
                                  validation.setFieldValue(
                                    "settings_website_demo_url",
                                    v
                                  )
                                }}
                                options={[
                                  {
                                    label:
                                      props.t("Undefined") +
                                      " / " +
                                      props.t("Other"),
                                    value: "",
                                  },
                                  {
                                    label: props.t("Classic"),
                                    value:
                                      "https://suvantocare-webrtcapi.arcticcommunicator.com/?82-1=",
                                  },
                                  {
                                    label: props.t("Classic demo"),
                                    value:
                                      "https://suvantocare-webrtcapidemo.arcticcommunicator.com/?82-1=",
                                  },
                                  {
                                    label: props.t("Istekki"),
                                    value:
                                      "https://istekki.arcticcommunicator.com/?82-1=",
                                  },
                                  {
                                    label: props.t("Istekki demo"),
                                    value:
                                      "https://istekkidemo.arcticcommunicator.com/?82-1=",
                                  },
                                ]}
                                style={{ width: 100 + "%" }}
                              />
                              <AntdForm.Item
                                style={{ marginBottom: "0px" }}
                                validateStatus={
                                  validation.errors.settings_website_demo_url
                                    ? "error"
                                    : ""
                                }
                                hasFeedback
                                help={
                                  validation.errors.settings_website_demo_url
                                }
                              >
                                <TextArea
                                  className="mt-2"
                                  allowClear
                                  autoSize={{ minRows: 3, maxRows: 5 }}
                                  placeholder={props.t("URL")}
                                  value={
                                    validation.values.settings_website_demo_url
                                  }
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "settings_website_demo_url",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </div>
                          </Col>
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}
                <Alert color="info" className="mb-3">
                  <p className="m-0">{props.t("onlyFilledSettingsApplied")}</p>
                </Alert>
                <Divider
                  orientation="left"
                  className="font-size-14 mt-3 text-primary"
                  orientationMargin="0"
                >
                  {props.t("General")}
                </Divider>
                <Row xs={1}>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Language")}</Label>
                      <Col sm={gridCols[1]}>
                        <Select
                          value={validation.values.settings_locale_language}
                          onChange={v => {
                            validation.setFieldValue(
                              "settings_locale_language",
                              v
                            )
                          }}
                          options={[
                            { label: props.t("Undefined"), value: "" },
                            { label: props.t("Finnish"), value: "FI" },
                            { label: props.t("English"), value: "EN" },
                            { label: props.t("Swedish"), value: "SV" },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>
                        {props.t("Volume buttons")}
                      </Label>
                      <Col sm={gridCols[1]}>
                        <Select
                          value={
                            validation.values
                              .settings_device_volume_buttons_enabled
                          }
                          onChange={v => {
                            validation.setFieldValue(
                              "settings_device_volume_buttons_enabled",
                              v
                            )
                          }}
                          options={[
                            { label: props.t("Undefined"), value: "" },
                            { label: props.t("Enabled"), value: true },
                            { label: props.t("Disabled"), value: false },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Volume")}</Label>
                      <Col sm={gridCols[1]}>
                        <Slider
                          min={3}
                          max={10}
                          onChange={v => {
                            validation.setFieldValue(
                              "settings_device_volume_value",
                              v
                            )
                          }}
                          value={
                            typeof validation.values
                              .settings_device_volume_value === "number"
                              ? validation.values.settings_device_volume_value
                              : 0
                          }
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>
                        {props.t("Screen brightness")}
                      </Label>
                      <Col sm={gridCols[1]}>
                        <Slider
                          min={0}
                          max={1}
                          step={0.01}
                          onChange={v => {
                            validation.setFieldValue(
                              "settings_device_screen_brightness",
                              v
                            )
                          }}
                          value={
                            typeof validation.values
                              .settings_device_screen_brightness === "number"
                              ? validation.values
                                  .settings_device_screen_brightness
                              : 0
                          }
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
                <Divider
                  orientation="left"
                  className="font-size-14 mt-3 text-primary"
                  orientationMargin="0"
                >
                  {props.t("Auto answer")}
                </Divider>
                <Row xs={1}>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Status")}</Label>
                      <Col sm={gridCols[1]}>
                        <Select
                          value={validation.values.automaticPickup}
                          onChange={v => {
                            validation.setFieldValue("automaticPickup", v)
                          }}
                          options={[
                            { label: props.t("Undefined"), value: "" },
                            { label: props.t("Enabled"), value: true },
                            { label: props.t("Disabled"), value: false },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Answer time")}</Label>
                      <Col sm={gridCols[1]}>
                        <InputNumber
                          //size="small"
                          style={{ width: "100%" }}
                          min={1}
                          max={120}
                          suffix={props.t("seconds")}
                          value={validation.values.answerTime}
                          onChange={newVal => {
                            validation.setFieldValue("answerTime", newVal)
                          }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
                <Divider
                  orientation="left"
                  className="font-size-14 mt-3 text-primary"
                  orientationMargin="0"
                >
                  {props.t("Screensaver")}
                </Divider>
                <Row xs={1}>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Content")}</Label>
                      <Col sm={gridCols[1]}>
                        <Select
                          value={screensaverContent}
                          onChange={v => {
                            setScreensaverContent(v)
                            switch (v) {
                              case "nothing":
                                validation.setFieldValue("clockEnabled", false)
                                validation.setFieldValue(
                                  "analogClockEnabled",
                                  false
                                )
                                break
                              case "analog":
                                validation.setFieldValue("clockEnabled", true)
                                validation.setFieldValue(
                                  "analogClockEnabled",
                                  true
                                )
                                break
                              case "digital":
                                validation.setFieldValue("clockEnabled", true)
                                validation.setFieldValue(
                                  "analogClockEnabled",
                                  false
                                )
                                break
                              default:
                                validation.setFieldValue("clockEnabled", null)
                                validation.setFieldValue(
                                  "analogClockEnabled",
                                  null
                                )
                                break
                            }
                          }}
                          options={[
                            { label: props.t("Undefined"), value: "" },
                            { label: props.t("Nothing"), value: "nothing" },
                            { label: props.t("Analog clock"), value: "analog" },
                            {
                              label: props.t("Digital clock"),
                              value: "digital",
                            },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Delay")}</Label>
                      <Col sm={gridCols[1]}>
                        <InputNumber
                          //className="me-2"
                          style={{ width: "100%" }}
                          min={1}
                          max={60}
                          addonAfter={
                            <Select
                              value={dimUnit}
                              style={{ minWidth: "80px" }}
                              onChange={v => {
                                setDimUnit(v)
                                validation.setFieldValue("usesDimMinutes", null)
                                validation.setFieldValue("dimSeconds", null)
                                validation.setFieldValue("dimMinutes", null)
                              }}
                            >
                              <Select.Option value="minutes">min</Select.Option>
                              <Select.Option value="seconds">s</Select.Option>
                            </Select>
                          }
                          value={
                            dimUnit == "seconds"
                              ? validation.values.dimSeconds
                              : validation.values.dimMinutes
                          }
                          onChange={newVal => {
                            if (newVal == null) {
                              validation.setFieldValue("usesDimMinutes", null)
                              validation.setFieldValue("dimSeconds", null)
                              validation.setFieldValue("dimMinutes", null)
                            } else if (dimUnit == "seconds") {
                              validation.setFieldValue("dimSeconds", newVal)
                              validation.setFieldValue("usesDimMinutes", false)
                            } else if (dimUnit == "minutes") {
                              validation.setFieldValue("dimMinutes", newVal)
                              validation.setFieldValue("usesDimMinutes", true)
                            }
                          }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row>
                      <Label sm={gridCols[0]}>{props.t("Active period")}</Label>
                      <Col sm={gridCols[1]}>
                        <ConfigProvider locale={locale}>
                          <TimePicker.RangePicker
                            //className="me-2"
                            style={{ width: "100%" }}
                            value={daytimeRange}
                            onChange={v => {
                              setDaytimeRange(v)
                              if (v != null) {
                                let mins = new Date(v[0]).getMinutes()
                                let hours = new Date(v[0]).getHours()
                                validation.setFieldValue(
                                  "morningTimeHour",
                                  hours
                                )
                                validation.setFieldValue(
                                  "morningTimeMinute",
                                  mins
                                )

                                mins = new Date(v[1]).getMinutes()
                                hours = new Date(v[1]).getHours()
                                validation.setFieldValue("nightTimeHour", hours)
                                validation.setFieldValue(
                                  "nightTimeMinute",
                                  mins
                                )
                              } else {
                                validation.setFieldValue(
                                  "morningTimeHour",
                                  null
                                )
                                validation.setFieldValue(
                                  "morningTimeMinute",
                                  null
                                )
                                validation.setFieldValue("nightTimeHour", null)
                                validation.setFieldValue(
                                  "nightTimeMinute",
                                  null
                                )
                              }
                            }}
                            format={"HH:mm"}
                          />
                        </ConfigProvider>
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
                <Divider
                  orientation="left"
                  className="font-size-14 mt-3 text-primary"
                  orientationMargin="0"
                >
                  {props.t("Other")}
                </Divider>
                <Row xs={1}>
                  <Col>
                    <FormGroup row className="align-items-center">
                      <Label sm={gridCols[0]}>
                        {props.t("Restart Video app")}
                      </Label>
                      <Col sm={gridCols[1]}>
                        <ConfigProvider locale={locale}>
                          <TimePicker
                            style={{ width: "100%" }}
                            value={restartTime}
                            onChange={v => {
                              setRestartTime(v)
                              if (v != null) {
                                let mins = new Date(v).getMinutes()
                                let hours = new Date(v).getHours()
                                validation.setFieldValue(
                                  "restartTimeHour",
                                  hours
                                )
                                validation.setFieldValue(
                                  "restartTimeMinute",
                                  mins
                                )
                              } else {
                                validation.setFieldValue(
                                  "restartTimeHour",
                                  null
                                )
                                validation.setFieldValue(
                                  "restartTimeMinute",
                                  null
                                )
                              }
                            }}
                            format={"HH:mm"}
                          />
                        </ConfigProvider>
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup row className="align-items-center">
                      <Label sm={gridCols[0]}>{props.t("PiP")}</Label>
                      <Col sm={gridCols[1]}>
                        <Select
                          value={validation.values.applicationPipShown}
                          onChange={v => {
                            validation.setFieldValue("applicationPipShown", v)
                          }}
                          options={[
                            { label: props.t("Undefined"), value: "" },
                            { label: props.t("Enabled"), value: true },
                            { label: props.t("Disabled"), value: false },
                          ]}
                          style={{ width: 100 + "%" }}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  {props.isSystemAdmin && (
                    <>
                      <Col>
                        <FormGroup row className="align-items-center">
                          <Label sm={gridCols[0]}>{"Health"}</Label>
                          <Col sm={gridCols[1]}>
                            <Select
                              value={validation.values.showHealthButton}
                              onChange={v => {
                                validation.setFieldValue("showHealthButton", v)
                              }}
                              options={[
                                { label: props.t("Undefined"), value: "" },
                                { label: props.t("Enabled"), value: true },
                                { label: props.t("Disabled"), value: false },
                              ]}
                              style={{ width: 100 + "%" }}
                            />
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row className="align-items-center">
                          <Label sm={gridCols[0]}>{props.t("Debug")}</Label>
                          <Col sm={gridCols[1]}>
                            <Select
                              value={validation.values.showDebugStats}
                              onChange={v => {
                                validation.setFieldValue("showDebugStats", v)
                              }}
                              options={[
                                { label: props.t("Undefined"), value: "" },
                                { label: props.t("Enabled"), value: true },
                                { label: props.t("Disabled"), value: false },
                              ]}
                              style={{ width: 100 + "%" }}
                            />
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row className="align-items-center">
                          <Label sm={gridCols[0]}>
                            {props.t("Assisted restart")}
                          </Label>
                          <Col sm={gridCols[1]}>
                            <Select
                              value={validation.values.uses_assisted_restart}
                              onChange={v => {
                                validation.setFieldValue(
                                  "uses_assisted_restart",
                                  v
                                )
                              }}
                              options={[
                                { label: props.t("Undefined"), value: "" },
                                { label: props.t("Enabled"), value: true },
                                { label: props.t("Disabled"), value: false },
                              ]}
                              style={{ width: 100 + "%" }}
                            />
                          </Col>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup row className="align-items-center">
                          <Label sm={gridCols[0]}>{props.t("Platform")}</Label>
                          <Col sm={gridCols[1]}>
                            <Select
                              value={validation.values.usesProduction}
                              onChange={v => {
                                validation.setFieldValue("usesProduction", v)
                              }}
                              options={[
                                { label: props.t("Undefined"), value: "" },
                                { label: props.t("Production"), value: true },
                                { label: props.t("Demo"), value: false },
                              ]}
                              style={{ width: 100 + "%" }}
                            />
                          </Col>
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>
              </SimpleBar>
            )}

            {currentStep == 1 && (
              <>
                <Divider
                  orientation="left"
                  className="font-size-14 mt-1"
                  orientationMargin="0"
                >
                  {props.t("Changes")}
                </Divider>

                <SimpleBar className="pe-3" style={{ maxHeight: "50vh" }}>
                  <Table
                    style={{ tableLayout: "fixed" }}
                    className="table"
                    bordered={false}
                    striped={false}
                    hover={true}
                  >
                    <tbody>
                      {changesTable.map((item, index) => {
                        return (
                          <tr key={"change_" + index}>
                            <th scope="row">{item.loc}</th>
                            <td>{item.value}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                  {/** 
                  <Alert color="info" className="mt-2" style={{ overflowWrap: "anywhere" }}>
                    <h6 className="alert-heading">
                      {props.t("Debugging")}
                    </h6>
                    <div>{changedValuesString}</div>
                  </Alert>*/}
                </SimpleBar>

                <Divider
                  orientation="left"
                  className="font-size-14 mt-2"
                  orientationMargin="0"
                >
                  {props.t("Receivers")}
                </Divider>
                <div>
                  {displayedNames.map((name, index) => (
                    <Chip
                      size="small"
                      key={"" + index}
                      className="mt-0 mb-2 font-size-12 me-1"
                      label={name.name}
                    ></Chip>
                  ))}
                  {remainingNames > 0 && !showAll && (
                    <Chip
                      size="small"
                      className="mt-0 mb-2 font-size-12 me-1"
                      label={`+ ${remainingNames} ${props.t("others")}`}
                    />
                  )}
                  {remainingNames > 0 && (
                    <button
                      onClick={toggleShowAll}
                      className="btn btn-link btn-sm"
                    >
                      {showAll ? props.t("Hide") : props.t("Show all")}
                    </button>
                  )}
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter className="">
            <div className="float-end">
              <button
                type="button"
                onClick={() => {
                  props.setShowModal(false)
                }}
                className="btn btn-outline-primary "
                data-dismiss="modal"
              >
                {props.t("Cancel")}
              </button>

              {currentStep == 0 && (
                <>
                  <Popconfirm
                    title={props.t("Reset form")}
                    onConfirm={() => resetFormValues()}
                  >
                    <button className="btn btn-outline-danger ms-2">
                      {props.t("Reset form")}
                    </button>
                  </Popconfirm>
                  <button
                    disabled={changedProps == 0}
                    className="btn btn-primary ms-2"
                    onClick={() => {
                      setCurrentStep(1)
                    }}
                  >
                    {props.t("Next")}
                  </button>
                </>
              )}

              {currentStep == 1 && (
                <>
                  <button
                    className="btn btn-primary ms-2"
                    onClick={() => {
                      setCurrentStep(0)
                    }}
                  >
                    {props.t("Previous")}
                  </button>
                  <Popconfirm
                    title={props.t("Change settings")}
                    onConfirm={() => {
                      validation.submitForm()
                    }}
                    okType="success"
                    okText={props.t("Send")}
                    cancelText={props.t("Cancel")}
                  >
                    <button
                      disabled={props.keys?.length == 0 || changedProps == 0}
                      className="btn btn-primary ms-2"
                    >
                      {props.t("Send")}
                    </button>
                  </Popconfirm>
                </>
              )}
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  )
}

VideoAppSettingsModal.propTypes = {
  t: PropTypes.any,
  keys: PropTypes.any,
  clients: PropTypes.any,
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func,
  isSystemAdmin: PropTypes.bool,
  setFailed: PropTypes.func,
}

export default withRouter(withTranslation()(VideoAppSettingsModal))
